package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.roster.RequestPlaceDto
import com.evoapps.evoshift.data.dto.roster.WorkRequestDto
import com.evoapps.evoshift.data.mapper.MetadataMapper
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import dev.gitlive.firebase.firestore.DocumentSnapshot
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map

/** SH-223: `work_requests` and `workplaces/{id}/work_places` (firestore.rules: who reads and writes them). */
class WorkRequestRepositoryImpl(
    private val firestore: AppFirestore,
    private val metadataMapper: MetadataMapper
) : WorkRequestRepository {

    private val requests get() = firestore.collection(COLLECTION)
    private fun placesOf(workplaceId: String) = firestore.collection("workplaces").document(workplaceId).collection(PLACES)

    fun toDto(request: WorkRequest) = WorkRequestDto(
        id = request.id,
        userId = request.userId,
        workplaceId = request.workplaceId,
        kind = request.kind.name,
        status = request.status.name,
        startDate = request.startDate,
        endDate = request.endDate,
        startTime = request.startTime.takeIf { request.isHourly },
        endTime = request.endTime.takeIf { request.isHourly },
        placeId = request.placeId,
        placeName = request.placeName,
        note = request.note,
        rejectionReason = request.rejectionReason,
        processedBy = request.processedBy,
        processedAt = request.processedAt,
        metadata = metadataMapper.toDto(request.metadata)
    )

    /** Null for a document this version cannot read (a later version's kind): it is left out. */
    fun toDomain(dto: WorkRequestDto, id: String): WorkRequest? {
        val kind = WorkRequestKind.fromName(dto.kind) ?: return null
        return WorkRequest(
            id = id,
            userId = dto.userId,
            workplaceId = dto.workplaceId,
            kind = kind,
            status = AbsenceStatus.entries.firstOrNull { it.name == dto.status } ?: AbsenceStatus.PENDING,
            startDate = dto.startDate,
            endDate = dto.endDate,
            startTime = dto.startTime?.takeIf { it.isNotBlank() && !dto.endTime.isNullOrBlank() },
            endTime = dto.endTime?.takeIf { it.isNotBlank() && !dto.startTime.isNullOrBlank() },
            placeId = dto.placeId,
            placeName = dto.placeName,
            note = dto.note,
            rejectionReason = dto.rejectionReason,
            processedBy = dto.processedBy,
            processedAt = dto.processedAt,
            metadata = metadataMapper.toDomain(dto.metadata)
        )
    }

    private fun List<DocumentSnapshot>.decoded(): List<WorkRequest> =
        mapNotNull { doc -> runCatching { toDomain(doc.data<WorkRequestDto>(), doc.id) }.getOrNull() }
            .sortedByDescending { it.startDate }

    override suspend fun create(request: WorkRequest): Result<String> = runCatching {
        requests.add(toDto(request.copy(id = ""))).id
    }

    override suspend fun update(request: WorkRequest): Result<Unit> = runCatching {
        requests.document(request.id).set(toDto(request))
    }

    override suspend fun delete(requestId: String): Result<Unit> = runCatching {
        requests.document(requestId).delete()
    }

    override suspend fun forUser(userId: String): Result<List<WorkRequest>> = runCatching {
        requests.where { "userId" equalTo userId }.get().documents.decoded()
    }

    /** SH-224: one query for the timeline, as AbsenceRepositoryImpl.getAbsencesOfUserBetween (the same index). */
    override suspend fun forUserBetween(userId: String, workplaceId: String, from: String, to: String): Result<List<WorkRequest>> = runCatching {
        requests.where { "userId" equalTo userId }
            .where { "workplaceId" equalTo workplaceId }
            .where { "endDate" greaterThanOrEqualTo from }
            .get().documents.decoded()
            .filter { it.startDate <= to }
    }

    override suspend fun forWorkplace(workplaceId: String): Result<List<WorkRequest>> = runCatching {
        requests.where { "workplaceId" equalTo workplaceId }.get().documents.decoded()
    }

    override fun observeForWorkplace(workplaceId: String): Flow<List<WorkRequest>> =
        requests.where { "workplaceId" equalTo workplaceId }
            .safeSnapshots
            .map { it.documents.decoded() }
            // Not a manager here (any more): nothing to decide.
            .catch { e -> if (e.isPermissionDenied()) emit(emptyList()) else throw e }
            .distinctUntilChanged()

    override suspend fun places(workplaceId: String): Result<List<RequestPlace>> = runCatching {
        placesOf(workplaceId).get().documents.mapNotNull { doc ->
            runCatching { doc.data<RequestPlaceDto>() }.getOrNull()?.let { RequestPlace(doc.id, it.name, it.createdBy, it.createdAt) }
        }.sortedBy { it.name.lowercase() }
    }

    override suspend fun addPlace(workplaceId: String, place: RequestPlace): Result<RequestPlace> = runCatching {
        val ref = placesOf(workplaceId).add(RequestPlaceDto(place.name, place.createdBy, place.createdAt))
        place.copy(id = ref.id)
    }

    override suspend fun renamePlace(workplaceId: String, placeId: String, name: String): Result<Unit> = runCatching {
        placesOf(workplaceId).document(placeId).updateFields { "name" to name }
    }

    override suspend fun removePlace(workplaceId: String, placeId: String): Result<Unit> = runCatching {
        placesOf(workplaceId).document(placeId).delete()
    }

    companion object {
        const val COLLECTION = "work_requests"
        const val PLACES = "work_places"
    }
}
